import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import RoutePage from "./components/Auth/Routes/RoutePage";
import { jwtDecode } from "jwt-decode";
import { setDayjsLocale } from "./dayjsConfig";
import { ToastContainer } from "react-toastify";
import { EndPoints } from "./utils/apiEndpoints";
import { Layout } from "./components/Pages/Layout";
import { useUserStore } from "./store/useUserStore";
import { useRoleStore } from "./store/useRoleStore";
import { useLanguageStore } from "./store/useLanguageStore";
import { useDarkModeStore } from "./store/useDarkModeStore";
import { useNavigate, useLocation } from "react-router-dom";
import { refreshTokenApiCall } from "./services/LoginService";
import { expiredCokkieInDay, StorageItems } from "./utils/storageItems";
import { getClaim, ROLE_CLAIM, TokenPayload } from "./domain/helpers/TokenPayload";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const previousLocationRef = useRef<string | null>(null);

  let token = Cookies.get(StorageItems.TOKEN);
  const refreshToken = Cookies.get(StorageItems.REFRESHTOKEN);

  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const { setRole } = useRoleStore();
  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();
  const { user, setUser } = useUserStore();

  const expiredToken = () => {
    setUser(null);
    Cookies.remove(StorageItems.TOKEN);
    Cookies.remove(StorageItems.REFRESHTOKEN);
    navigate(EndPoints.LOGIN);
    setSessionExpired(true);
  }

  const checkRefreshToken = async () => {
    if (token && refreshToken) {
      const response = await refreshTokenApiCall(token, refreshToken);
      if (response) {
        token = response.token;
        Cookies.set(StorageItems.TOKEN, response.token, { expires: expiredCokkieInDay });
        const decodedToken = jwtDecode(response.token);

        setRole(getClaim(decodedToken as TokenPayload, ROLE_CLAIM as keyof TokenPayload)?.toString() ?? "");
        setUser(decodedToken as TokenPayload);
      }
      else { expiredToken(); }
    }
    else { expiredToken(); }
    setTimeout(() => { setLoading(false); }, 300);
  }

  useEffect(() => {
    setDayjsLocale(language['Language'] === 'English' ? 'en-gb' : 'sq');
  }, [language]);

  useEffect(() => {
    if (pathname === EndPoints.JWTAPI || pathname === EndPoints.ONBOARDING) {
      setLoading(false);
    }
    else if (token) {
      const decodedToken = jwtDecode(token);
      navigate(pathname === EndPoints.LOGIN ? '/' : pathname + search);
      setUser(decodedToken as TokenPayload);
      setRole(getClaim(decodedToken as TokenPayload, ROLE_CLAIM as keyof TokenPayload)?.toString() ?? "");
      setSessionExpired(false);
    }
    else { expiredToken(); }

    setTimeout(() => { setLoading(false); }, 300);
  }, [token]);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    localStorage.setItem('PreviousLocation', previousLocation || "");
    previousLocationRef.current = pathname;

    if (user && user.exp && !sessionExpired) {
      const expiredDate = dayjs(new Date());
      const currentDate = dayjs(new Date(user.exp * 1000));

      if (currentDate.isBefore(expiredDate)) checkRefreshToken();
    }
  }, [pathname]);

  return loading
    ? <div
      className={`w-100 d-flex flex-row ${darkMode ? 'dark-mode' : 'light-mode'}`}
      style={{ height: '-webkit-fill-available' }}
    />
    : <div
      className={`w-100 d-flex flex-row ${darkMode ? 'dark-mode' : 'light-mode'}`}
      style={{ height: '-webkit-fill-available' }}
    >
      <ToastContainer></ToastContainer>
      <Layout>
        <RoutePage user={user} />
      </Layout>
    </div>
}

export default App;